import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import NoteIcon from '@mui/icons-material/Note';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Client,
  ClientActivity,
  ClientPreference,
  ClientEngagementProgress,
  ClientCampaignProgress,
  fetchClientDetail,
  fetchClientEngagement,
  sendNextEngagementDayApi,
  resetClientEngagement,
  fetchClientCampaigns,
  setClientCampaignEnrolled,
  sendNextClientCampaignDay,
  resetClientCampaign,
  addActivity,
  savePreference,
  findMatches,
  parseHypingProjects,
  updateClientStage,
  setClientEngagementActive,
  STAGES,
  STAGE_LABELS,
  STAGE_COLORS,
  WHATSAPP_UNREACHABLE_HINT,
  Stage,
  ACTIVITY_TYPES,
  fetchWhatsAppCloudStatus,
  fetchAgentSettings,
  fetchCampaigns,
  fetchMyCampaigns,
  AgentSettings,
  MyCampaign,
  WhatsAppCloudStatus,
} from '../services/api';
import { useMobileMask } from '../context/MobileMaskContext';
import {
  evaluateSevenDayReadiness,
  evaluateCustomCampaignReadiness,
  engagementDaysFromCampaignRows,
  campaignRowHasContent,
  readinessWarningText,
  CampaignReadiness,
} from '../utils/campaignReadiness';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Alert from '@mui/material/Alert';
import QuickCreateClient from '../components/QuickCreateClient';
import WhatsAppComposeDialog from '../components/WhatsAppComposeDialog';

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  note: <NoteIcon fontSize="small" />,
  call: <PhoneIcon fontSize="small" />,
  meeting: <HandshakeIcon fontSize="small" />,
  viewing: <VisibilityIcon fontSize="small" />,
  whatsapp: <WhatsAppIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
  'stage-change': <EventIcon fontSize="small" />,
  // Logged by the server when a share-link visitor asks for something (valuation, nearby promo, …).
  'client-request': <CampaignIcon fontSize="small" />,
};

const ACTIVITY_COLORS: Record<string, string> = {
  note: '#6b7280',
  call: '#3b82f6',
  meeting: '#8b5cf6',
  viewing: '#f59e0b',
  whatsapp: '#25D366',
  email: '#ef4444',
  'stage-change': '#ed8936',
  'client-request': '#d79d55',
};

/** Chip text where the raw type reads badly; others show the type itself. */
const ACTIVITY_LABELS: Record<string, string> = {
  'client-request': 'Client request',
};

function getCampaignDayState(
  exec: { Status?: string; LastError?: string | null; SentAt?: string | Date | null } | undefined,
  dayNum: number,
  lastSentDay: number,
) {
  const fallbackSent = !exec && dayNum <= lastSentDay;
  const status = exec?.Status || (fallbackSent ? 'sent' : 'pending');
  const sent = status === 'sent';
  const failed = status === 'failed';
  const title = exec?.LastError
    ? exec.LastError
    : exec?.SentAt
      ? `Sent ${new Date(exec.SentAt).toLocaleString()}`
      : fallbackSent
        ? 'Marked sent via reset'
        : status;
  return { sent, failed, title };
}

export default function ClientDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatMobile } = useMobileMask();
  // Extract client ID from path: /super-connector/clients/:id
  const id = location.pathname.split('/clients/')[1]?.split('/')[0] || undefined;
  const [client, setClient] = useState<Client | null>(null);
  const [activities, setActivities] = useState<ClientActivity[]>([]);
  const [preferences, setPreferences] = useState<ClientPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [actType, setActType] = useState('note');
  const [actDescription, setActDescription] = useState('');
  const [showPrefDialog, setShowPrefDialog] = useState(false);
  const [prefType, setPrefType] = useState<string>('buyer');
  const [prefData, setPrefData] = useState<Partial<ClientPreference>>({});
  const [matches, setMatches] = useState<Client[]>([]);
  const [showMatches, setShowMatches] = useState(false);

  const [waComposeOpen, setWaComposeOpen] = useState(false);

  // 7-Day Engagement progress + manual trigger state
  const [engagement, setEngagement] = useState<ClientEngagementProgress | null>(null);
  const [engagementBusy, setEngagementBusy] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);
  const [engagementInfo, setEngagementInfo] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetNextDay, setResetNextDay] = useState<number>(1);

  // Custom campaigns granted to this agent (per-client enrol + manual send)
  const [clientCampaigns, setClientCampaigns] = useState<ClientCampaignProgress[]>([]);
  const [sevenDayReadiness, setSevenDayReadiness] = useState<CampaignReadiness>({ canActivate: true, reasons: [] });
  const [customReadinessByName, setCustomReadinessByName] = useState<Record<string, CampaignReadiness>>({});

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const result = await fetchClientDetail(id);
      setClient(result.client);
      setActivities(result.activities);
      setPreferences(result.preferences);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadEngagement = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchClientEngagement(id);
      setEngagement(data);
    } catch (e) {
      console.error(e);
    }
  }, [id]);

  const loadClientCampaigns = useCallback(async () => {
    if (!id) return;
    try {
      setClientCampaigns(await fetchClientCampaigns(id));
    } catch (e) {
      console.error(e);
    }
  }, [id]);

  const loadCampaignReadiness = useCallback(async () => {
    try {
      const [cloudStatus, settings, camps, myCamps] = await Promise.all([
        fetchWhatsAppCloudStatus().catch(() => null as WhatsAppCloudStatus | null),
        fetchAgentSettings().catch(() => null as AgentSettings | null),
        fetchCampaigns().catch(() => null),
        fetchMyCampaigns().catch(() => [] as MyCampaign[]),
      ]);
      const usingCloud = cloudStatus?.provider === 'cloud';
      const hasCalendly = !!(settings?.hasCalendlyKey && settings?.calendlyEventTypeUri);
      const engagementRows = camps?.grouped?.['7-Day Engagement'] ?? [];
      setSevenDayReadiness(evaluateSevenDayReadiness({
        days: engagementDaysFromCampaignRows(engagementRows),
        agentSettings: settings,
        usingCloud: !!usingCloud,
        templates: cloudStatus?.templates ?? [],
      }));
      const byName: Record<string, CampaignReadiness> = {};
      for (const c of myCamps) {
        byName[c.name] = evaluateCustomCampaignReadiness({
          usingCloud: !!usingCloud,
          hasCalendly,
          days: c.days.map((d) => ({
            dayNumber: d.DayNumber,
            status: d.Status,
            hasContent: campaignRowHasContent(d),
            useCalendly: !!d.UseCalendly,
          })),
          numDays: c.numDays,
        });
      }
      setCustomReadinessByName(byName);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEngagement(); }, [loadEngagement]);
  useEffect(() => { loadClientCampaigns(); }, [loadClientCampaigns]);
  useEffect(() => { loadCampaignReadiness(); }, [loadCampaignReadiness]);

  const handleReset = async () => {
    if (!id) return;
    setEngagementBusy(true);
    setEngagementError(null);
    setEngagementInfo(null);
    try {
      await resetClientEngagement(id, resetNextDay);
      setEngagementInfo(resetNextDay === 1
        ? 'Engagement reset to Day 1.'
        : `Engagement reset — next send will be Day ${resetNextDay}.`);
      setShowResetDialog(false);
      await Promise.all([loadEngagement(), load()]);
    } catch (e) {
      setEngagementError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setEngagementBusy(false);
    }
  };

  const handleManualSend = async () => {
    if (!id) return;
    if (!sevenDayReadiness.canActivate) return;
    setEngagementBusy(true);
    setEngagementError(null);
    setEngagementInfo(null);
    try {
      const r = await sendNextEngagementDayApi(id);
      if (!r.ok) {
        setEngagementError(r.error || 'Send failed');
      } else {
        setEngagementInfo(`Day ${r.dayNumber} sent.`);
        await Promise.all([loadEngagement(), load()]);
      }
    } catch (e) {
      setEngagementError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setEngagementBusy(false);
    }
  };

  const handleStageChange = async (newStage: string) => {
    if (!id || !client || newStage === client.Stage) return;
    const prev = client.Stage;
    setClient({ ...client, Stage: newStage });
    try {
      await updateClientStage(id, newStage);
      load();
    } catch (e) {
      console.error(e);
      setClient({ ...client, Stage: prev });
    }
  };

  const handleToggleEngagement = async (active: boolean) => {
    if (!id || !client) return;
    if (active && !sevenDayReadiness.canActivate) return;
    const prev = client.EngagementActive ?? false;
    setClient({ ...client, EngagementActive: active });
    setEngagementError(null);
    try {
      await setClientEngagementActive(id, active);
      await loadEngagement();
    } catch (e) {
      console.error(e);
      setClient({ ...client, EngagementActive: prev });
      setEngagementError(e instanceof Error ? e.message : 'Failed to update engagement');
    }
  };

  const handleAddActivity = async () => {
    if (!id) return;
    try {
      await addActivity(id, { Type: actType, Description: actDescription });
      setShowAddActivity(false);
      setActDescription('');
      load();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSavePref = async () => {
    if (!id) return;
    try {
      await savePreference(id, {
        PreferenceType: prefType,
        BudgetMin: prefData.BudgetMin ? Number(prefData.BudgetMin) : undefined,
        BudgetMax: prefData.BudgetMax ? Number(prefData.BudgetMax) : undefined,
        Area: prefData.Area || undefined,
        PropertyType: prefData.PropertyType || undefined,
        SellingAddress: prefData.SellingAddress || undefined,
        SellingPrice: prefData.SellingPrice ? Number(prefData.SellingPrice) : undefined,
      });
      setShowPrefDialog(false);
      setPrefData({});
      load();
    } catch (e) {
      console.error(e);
    }
  };

  const handleFindMatches = async () => {
    if (!id) return;
    try {
      const result = await findMatches(id);
      setMatches(result.matches);
      setShowMatches(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddToCalendar = () => {
    if (!client?.DOB || !client?.Name) return;
    const dob = new Date(client.DOB);
    const title = encodeURIComponent(`${client.Name}'s Birthday`);
    const nextBday = new Date(new Date().getFullYear(), dob.getMonth(), dob.getDate());
    if (nextBday < new Date()) nextBday.setFullYear(nextBday.getFullYear() + 1);
    const dateStr = nextBday.toISOString().split('T')[0].replace(/-/g, '');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dateStr}/${dateStr}&recur=RRULE:FREQ=YEARLY`;
    window.open(url, '_blank');
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!client) {
    return <Typography color="error">Client not found</Typography>;
  }

  // ─── Derived chart data ───
  const INSIGHT_ACTIVITY_TYPES = ['whatsapp'] as const;
  const activityTypeCounts = INSIGHT_ACTIVITY_TYPES.map((t) => ({
    type: t,
    label: t.charAt(0).toUpperCase() + t.slice(1),
    count: activities.filter((a) => a.Type === t).length,
    color: ACTIVITY_COLORS[t] || '#888',
  })).filter((d) => d.count > 0);

  // Engagement over last 14 days
  const timelineData = (() => {
    const days = 14;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const buckets: Array<{ date: string; label: string; count: number }> = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      buckets.push({
        date: key,
        label: `${d.getMonth() + 1}/${d.getDate()}`,
        count: 0,
      });
    }
    activities.forEach((a) => {
      const d = new Date(a.Date);
      d.setHours(0, 0, 0, 0);
      const key = d.toISOString().slice(0, 10);
      const bucket = buckets.find((b) => b.date === key);
      if (bucket) bucket.count += 1;
    });
    return buckets;
  })();

  const engagementOn = client.EngagementActive ?? false;
  const initials = client.Name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const lastSentDay = engagement?.lastSentDay ?? 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/super-connector/clients')} sx={{ color: 'var(--color-text-subtle)' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>{client.Name}</Typography>
        <Chip
          label={STAGE_LABELS[client.Stage as Stage] || client.Stage}
          size="small"
          sx={{
            bgcolor: `${STAGE_COLORS[client.Stage as Stage] || '#888'}22`,
            color: STAGE_COLORS[client.Stage as Stage] || '#888',
            fontWeight: 700,
          }}
        />
        {client.Type && (
          <Chip label={client.Type === 'buyer' ? 'Buyer' : 'Seller'} size="small" variant="outlined" />
        )}
        {client.WhatsAppUnreachableAt && (
          <Tooltip title={WHATSAPP_UNREACHABLE_HINT}>
            <Chip
              icon={<PhonelinkEraseIcon sx={{ fontSize: '0.9rem' }} />}
              label="Invalid WhatsApp Number"
              size="small"
              sx={{
                bgcolor: '#ef444422',
                color: '#ef4444',
                fontWeight: 700,
                '& .MuiChip-icon': { color: '#ef4444' },
              }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Pipeline</InputLabel>
          <Select
            label="Pipeline"
            value={client.Stage}
            onChange={(e) => handleStageChange(e.target.value)}
            renderValue={(v) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STAGE_COLORS[v as Stage] || '#888' }} />
                {STAGE_LABELS[v as Stage] || v}
              </Box>
            )}
          >
            {STAGES.map((s) => (
              <MenuItem key={s} value={s}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STAGE_COLORS[s] }} />
                  {STAGE_LABELS[s]}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        {/* Left column */}
        <Grid item xs={12} md={4}>
          {/* Profile Card */}
          <Card sx={{ bgcolor: 'var(--color-surface)', mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 56, height: 56, borderRadius: '50%', bgcolor: '#ed8936', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1.25rem', flexShrink: 0,
                  }}
                >
                  {initials}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>{client.Name}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={STAGE_LABELS[client.Stage as Stage] || client.Stage}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: `${STAGE_COLORS[client.Stage as Stage] || '#888'}22`,
                        color: STAGE_COLORS[client.Stage as Stage] || '#888',
                        fontWeight: 700,
                      }}
                    />
                    {client.Type && (
                      <Chip
                        label={client.Type === 'buyer' ? 'Buyer' : 'Seller'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                    {(client.Tags || []).map((t) => (
                      <Chip
                        key={t.Id}
                        label={t.Name}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: `${t.Color || '#ed8936'}22`,
                          color: t.Color || '#ed8936',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
                <Tooltip title="Edit client profile">
                  <IconButton size="small" onClick={() => setEditing(true)} sx={{ color: 'var(--color-text-subtle)' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {/* At-a-glance info */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', rowGap: 0.75, columnGap: 1, fontSize: '0.82rem' }}>
                {client.Mobile && (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Mobile</Typography>
                    <Box>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{formatMobile(client.Mobile)}</Typography>
                      {client.WhatsAppUnreachableAt && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#ef4444', fontWeight: 600 }}>
                          Invalid WhatsApp number — campaign messages cannot be delivered
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
                {client.Email && (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Email</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{client.Email}</Typography>
                  </>
                )}
                {client.DOB && (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>DOB</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">{new Date(client.DOB).toLocaleDateString()}</Typography>
                      <Tooltip title="Add birthday to Google Calendar">
                        <IconButton size="small" onClick={handleAddToCalendar} sx={{ color: '#ed8936', p: 0.25 }}>
                          <CalendarMonthIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {client.Address && (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Address</Typography>
                    <Typography variant="body2">{client.Address}</Typography>
                  </>
                )}
                {client.PropertyType && (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Property</Typography>
                    <Typography variant="body2">{client.PropertyType}</Typography>
                  </>
                )}
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Budget</Typography>
                <Typography variant="body2" sx={{ color: client.Budget ? 'inherit' : 'var(--color-text-faint)' }}>
                  {client.Budget ? `$${Number(client.Budget).toLocaleString()}` : '—'}
                </Typography>
                {(() => {
                  const metAt = client.CurrentLocation || '';
                  const preferredLoc = client.PreferredLocation || '';
                  let districts: string[] = [];
                  if (client.InterestedDistricts) {
                    try {
                      const parsed = JSON.parse(client.InterestedDistricts);
                      if (Array.isArray(parsed)) districts = parsed.filter((v) => typeof v === 'string');
                    } catch { /* ignore */ }
                  }
                  const distanceLabel = client.DistanceFromPreferredLocation != null
                    ? `Within ${client.DistanceFromPreferredLocation} km`
                    : '';
                  const interestedParts = [
                    districts.length > 0 ? districts.join(', ') : '',
                    distanceLabel,
                  ].filter(Boolean);
                  const interestedText = interestedParts.join(' · ');
                  return (
                    <>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Hyping Project</Typography>
                      {(() => {
                        const hypingNames = parseHypingProjects(client).map((p) => p.name);
                        return (
                          <Typography variant="body2" sx={{ color: hypingNames.length > 0 ? 'inherit' : 'var(--color-text-faint)' }}>
                            {hypingNames.length > 0 ? hypingNames.join(', ') : '—'}
                          </Typography>
                        );
                      })()}
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Met At</Typography>
                      <Typography variant="body2" sx={{ color: metAt ? 'inherit' : 'var(--color-text-faint)' }}>
                        {metAt || '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Preferred Location</Typography>
                      <Typography variant="body2" sx={{ color: preferredLoc ? 'inherit' : 'var(--color-text-faint)' }}>
                        {preferredLoc || '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Interested</Typography>
                      <Typography variant="body2" sx={{ color: interestedText ? 'inherit' : 'var(--color-text-faint)' }}>
                        {interestedText || '—'}
                      </Typography>
                    </>
                  );
                })()}
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Note</Typography>
                <Typography variant="body2" sx={{ color: client.Note ? 'var(--color-text-subtle)' : 'var(--color-text-faint)', whiteSpace: 'pre-wrap' }}>
                  {client.Note || '—'}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Quick Actions — labeled clearly */}
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                Reach Out
              </Typography>
              <Stack spacing={1}>
                {client.Mobile ? (
                  <Tooltip title={`Send a WhatsApp message to ${formatMobile(client.Mobile)}`} placement="top">
                    <Button
                      size="small"
                      variant="contained"
                      fullWidth
                      startIcon={<WhatsAppIcon />}
                      onClick={() => setWaComposeOpen(true)}
                      sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, textTransform: 'none', justifyContent: 'flex-start' }}
                    >
                      Send WhatsApp Message
                    </Button>
                  </Tooltip>
                ) : (
                  <Button size="small" variant="contained" fullWidth disabled startIcon={<WhatsAppIcon />} sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>
                    Add mobile to WhatsApp
                  </Button>
                )}
                {/* Temporarily hidden: Find Matching Sellers/Buyers button */}
                {false && (
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    startIcon={<PersonSearchIcon />}
                    onClick={handleFindMatches}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    Find Matching {client?.Type === 'buyer' ? 'Sellers' : 'Buyers'}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* 7-Day Engagement */}
          <Card sx={{ bgcolor: 'var(--color-surface)', mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutorenewIcon sx={{ color: engagementOn ? '#ed8936' : 'var(--color-text-faint)' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  7-Day Engagement
                </Typography>
                <Tooltip title={
                  !engagementOn && !sevenDayReadiness.canActivate
                    ? readinessWarningText(sevenDayReadiness.reasons)
                    : engagementOn
                      ? 'Automated engagement is ON — click to pause'
                      : 'Automated engagement is OFF — click to resume'
                }>
                  <span>
                  <Switch
                    size="small"
                    checked={engagementOn}
                    disabled={!engagementOn && !sevenDayReadiness.canActivate}
                    onChange={(e) => handleToggleEngagement(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                    }}
                  />
                  </span>
                </Tooltip>
                <Tooltip title="Reset engagement">
                  <IconButton
                    size="small"
                    onClick={() => { setResetNextDay(1); setShowResetDialog(true); }}
                    sx={{ color: 'var(--color-text-subtle)' }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {!engagementOn && !sevenDayReadiness.canActivate && (
                <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mb: 1 }}>
                  {readinessWarningText(sevenDayReadiness.reasons)}
                </Typography>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1.5 }}>
                {Array.from({ length: 7 }, (_, i) => i + 1).map((dayNum) => {
                  const exec = (engagement?.executions ?? []).find((e) => e.DayNumber === dayNum);
                  const { sent, failed, title } = getCampaignDayState(exec, dayNum, lastSentDay);
                  const colorBg = sent ? 'rgba(34,197,94,0.18)' : failed ? 'rgba(239,68,68,0.18)' : 'var(--color-bg)';
                  const colorFg = sent ? '#22c55e' : failed ? '#ef4444' : 'var(--color-text-subtle)';
                  return (
                    <Box
                      key={dayNum}
                      sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        py: 0.75, borderRadius: 1, bgcolor: colorBg, color: colorFg,
                        border: '1px solid var(--color-border)',
                        opacity: engagementOn ? 1 : 0.5,
                      }}
                      title={title}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>D{dayNum}</Typography>
                      <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center' }}>
                        {sent
                          ? <CheckCircleIcon sx={{ fontSize: 14 }} />
                          : failed
                            ? <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                            : <ScheduleIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
                {engagement?.lastSentAt
                  ? <>Last sent: <strong>Day {engagement.lastSentDay}</strong> on {new Date(engagement.lastSentAt).toLocaleString()}</>
                  : 'No engagement messages sent yet.'}
              </Typography>

              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={engagementBusy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon />}
                disabled={engagementBusy || !client.Mobile || lastSentDay >= 7 || !sevenDayReadiness.canActivate}
                onClick={handleManualSend}
                sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
              >
                {engagementBusy
                  ? 'Sending...'
                  : lastSentDay >= 7
                    ? 'All 7 days sent'
                    : `Send Day ${lastSentDay + 1} now`}
              </Button>
              {!client.Mobile && (
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mt: 0.5 }}>
                  Add a mobile number to enable engagement.
                </Typography>
              )}
              {client.Mobile && lastSentDay < 7 && !sevenDayReadiness.canActivate && engagementOn && (
                <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
                  {readinessWarningText(sevenDayReadiness.reasons)}
                </Typography>
              )}
              {engagementInfo && <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>{engagementInfo}</Alert>}
              {engagementError && <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>{engagementError}</Alert>}
            </CardContent>
          </Card>

          {/* Custom campaigns — one card each, mirroring the 7-Day controls */}
          {clientCampaigns.map((cc) => (
            <ClientCampaignCard
              key={cc.name}
              clientId={id || ''}
              progress={cc}
              hasMobile={!!client.Mobile}
              onChanged={loadClientCampaigns}
              readiness={customReadinessByName[cc.name] ?? { canActivate: true, reasons: [] }}
            />
          ))}

          {/* Insights: activity breakdown + trend */}
          <Card sx={{ bgcolor: 'var(--color-surface)', mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Engagement Insights
              </Typography>

              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 0.5 }}>
                Activity Breakdown
              </Typography>
              {activityTypeCounts.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', py: 2, textAlign: 'center' }}>
                  No activities yet
                </Typography>
              ) : (
                <Box sx={{ width: '100%', height: 140, mb: 2 }}>
                  <ResponsiveContainer>
                    <BarChart data={activityTypeCounts} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-text-strong)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-strong)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: '0.75rem' }}
                        cursor={{ fill: 'rgba(237, 137, 54, 0.08)' }}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {activityTypeCounts.map((d) => (
                          <Cell key={d.type} fill={d.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 0.5 }}>
                Last 14 Days
              </Typography>
              <Box sx={{ width: '100%', height: 120 }}>
                <ResponsiveContainer>
                  <LineChart data={timelineData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-text-strong)' }} axisLine={false} tickLine={false} interval={1} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-strong)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: '0.75rem' }}
                    />
                    <Line type="monotone" dataKey="count" stroke="#ed8936" strokeWidth={2} dot={{ r: 3, fill: '#ed8936' }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Timeline */}
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: 'var(--color-surface)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Activity Timeline</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                    {activities.length} {activities.length === 1 ? 'activity' : 'activities'} recorded
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddActivity(true)}
                  sx={{ textTransform: 'none', color: '#ed8936', borderColor: '#ed8936', '&:hover': { borderColor: '#dd7826', bgcolor: 'rgba(237, 137, 54, 0.08)' } }}
                >
                  Add Activity
                </Button>
              </Box>

              {activities.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', textAlign: 'center', py: 4 }}>
                  No activities yet
                </Typography>
              ) : (
                <Box sx={{ position: 'relative', pl: 3 }}>
                  {/* Timeline line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 11,
                      top: 8,
                      bottom: 8,
                      width: 2,
                      bgcolor: 'var(--color-border)',
                    }}
                  />
                  {activities.map((act) => (
                    <Box key={act.Id} sx={{ position: 'relative', mb: 2, display: 'flex', gap: 2 }}>
                      {/* Dot */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -22,
                          top: 4,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: ACTIVITY_COLORS[act.Type] || '#888',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}
                      >
                        {ACTIVITY_ICONS[act.Type] || <NoteIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                          <Chip
                            label={ACTIVITY_LABELS[act.Type] ?? act.Type}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              bgcolor: `${ACTIVITY_COLORS[act.Type] || '#888'}22`,
                              color: ACTIVITY_COLORS[act.Type] || '#888',
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                            {new Date(act.Date).toLocaleString()}
                          </Typography>
                        </Box>
                        {act.Description && (
                          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
                            {act.Description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Activity Dialog */}
      <Dialog
        open={showAddActivity}
        onClose={() => setShowAddActivity(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Add Activity</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select value={actType} onChange={(e) => setActType(e.target.value)} label="Type">
              {ACTIVITY_TYPES.filter((t) => t !== 'stage-change').map((t) => (
                <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={actDescription}
            onChange={(e) => setActDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddActivity(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddActivity} sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preference Dialog */}
      <Dialog
        open={showPrefDialog}
        onClose={() => setShowPrefDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Add/Update Preference</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            value={prefType}
            exclusive
            onChange={(_, v) => v && setPrefType(v)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          >
            <ToggleButton value="buyer" sx={{ textTransform: 'none' }}>Buying</ToggleButton>
            <ToggleButton value="seller" sx={{ textTransform: 'none' }}>Selling</ToggleButton>
          </ToggleButtonGroup>

          {prefType === 'buyer' ? (
            <>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField size="small" fullWidth label="Budget Min ($)" type="number"
                    onChange={(e) => setPrefData({ ...prefData, BudgetMin: Number(e.target.value) as any })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField size="small" fullWidth label="Budget Max ($)" type="number"
                    onChange={(e) => setPrefData({ ...prefData, BudgetMax: Number(e.target.value) as any })} />
                </Grid>
              </Grid>
              <TextField size="small" fullWidth label="Preferred Area" sx={{ mt: 1.5 }}
                onChange={(e) => setPrefData({ ...prefData, Area: e.target.value })} />
              <TextField size="small" fullWidth label="Property Type" sx={{ mt: 1.5 }}
                onChange={(e) => setPrefData({ ...prefData, PropertyType: e.target.value })} />
            </>
          ) : (
            <>
              <TextField size="small" fullWidth label="Selling Address" sx={{ mb: 1.5 }}
                onChange={(e) => setPrefData({ ...prefData, SellingAddress: e.target.value })} />
              <TextField size="small" fullWidth label="Selling Price ($)" type="number" sx={{ mb: 1.5 }}
                onChange={(e) => setPrefData({ ...prefData, SellingPrice: Number(e.target.value) as any })} />
              <TextField size="small" fullWidth label="Property Type"
                onChange={(e) => setPrefData({ ...prefData, PropertyType: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrefDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePref} sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Matches Dialog */}
      <Dialog
        open={showMatches}
        onClose={() => setShowMatches(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>
          Matching {client.Type === 'buyer' ? 'Sellers' : 'Buyers'}
        </DialogTitle>
        <DialogContent>
          {matches.length === 0 ? (
            <Typography sx={{ color: 'var(--color-text-faint)', py: 2, textAlign: 'center' }}>
              No matching clients found
            </Typography>
          ) : (
            matches.map((m) => (
              <Box key={m.Id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid var(--color-border)' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{m.Name}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                    {m.PropertyType}{m.Budget ? ` | $${Number(m.Budget).toLocaleString()}` : ''}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: 'var(--color-text-faint)' }}>
                    Agent: {m.CEANumber}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    const msg = `Hi, I have a ${client.Type === 'buyer' ? 'buyer' : 'seller'} interested in ${m.PropertyType || 'property'}. Would you like to co-broke?`;
                    const phone = m.CEANumber; // In real app, look up agent's phone
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Contact Agent
                </Button>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMatches(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Shared WhatsApp compose — same UX as the client-list row icon. */}
      <WhatsAppComposeDialog
        open={waComposeOpen}
        client={client}
        onClose={() => setWaComposeOpen(false)}
        onSent={() => load()}
      />

      {/* Reset 7-Day Engagement Dialog */}
      <Dialog
        open={showResetDialog}
        onClose={() => !engagementBusy && setShowResetDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Reset 7-Day Engagement</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
            Choose which day the next message should be. Days before the chosen
            day will be marked as already sent; days from the chosen day onward
            will be reset to pending.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Next send</InputLabel>
            <Select
              value={resetNextDay}
              label="Next send"
              onChange={(e) => setResetNextDay(Number(e.target.value))}
            >
              <MenuItem value={1}>Day 1 (full reset)</MenuItem>
              <MenuItem value={2}>Day 2</MenuItem>
              <MenuItem value={3}>Day 3</MenuItem>
              <MenuItem value={4}>Day 4</MenuItem>
              <MenuItem value={5}>Day 5</MenuItem>
              <MenuItem value={6}>Day 6</MenuItem>
              <MenuItem value={7}>Day 7</MenuItem>
              <MenuItem value={8}>Mark all 7 as sent (skip series)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowResetDialog(false)} disabled={engagementBusy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={engagementBusy}
            onClick={handleReset}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {engagementBusy ? 'Resetting...' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Drawer */}
      <QuickCreateClient
        open={editing}
        onClose={() => setEditing(false)}
        onCreated={() => { setEditing(false); load(); }}
        client={client}
      />
    </Box>
  );
}

// ─── Per-client custom campaign card (mirrors the 7-Day Engagement controls) ───
function ClientCampaignCard({
  clientId, progress, hasMobile, onChanged, readiness,
}: {
  clientId: string;
  progress: ClientCampaignProgress;
  hasMobile: boolean;
  onChanged: () => void;
  readiness: CampaignReadiness;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetNextDay, setResetNextDay] = useState<number>(1);
  const on = progress.enrolled;
  const lastSentDay = progress.lastSentDay ?? 0;
  const allSent = lastSentDay >= progress.numDays;
  const blockEnrol = !on && !readiness.canActivate;

  const toggle = async (active: boolean) => {
    if (active && !readiness.canActivate) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await setClientCampaignEnrolled(clientId, progress.name, active);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!readiness.canActivate) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await sendNextClientCampaignDay(clientId, progress.name);
      if (!r.ok) setError(r.error || 'Send failed');
      else { setInfo(`Day ${r.dayNumber} sent.`); onChanged(); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      await resetClientCampaign(clientId, progress.name, resetNextDay);
      setInfo(
        resetNextDay === 1
          ? `${progress.name} reset to Day 1.`
          : resetNextDay > progress.numDays
            ? `${progress.name} marked complete.`
            : `${progress.name} reset — next send will be Day ${resetNextDay}.`,
      );
      setShowResetDialog(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card sx={{ bgcolor: 'var(--color-surface)', mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CampaignIcon sx={{ color: on ? '#ed8936' : 'var(--color-text-faint)' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{progress.name}</Typography>
            <Tooltip title={
              blockEnrol
                ? readinessWarningText(readiness.reasons)
                : on
                  ? 'Enrolled — click to remove this client'
                  : 'Not enrolled — click to enrol this client'
            }>
              <span>
              <Switch
                size="small"
                checked={on}
                disabled={busy || blockEnrol}
                onChange={(e) => toggle(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                }}
              />
              </span>
            </Tooltip>
            <Tooltip title="Reset campaign">
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => { setResetNextDay(1); setShowResetDialog(true); }}
                sx={{ color: 'var(--color-text-subtle)' }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {blockEnrol && (
            <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mb: 1 }}>
              {readinessWarningText(readiness.reasons)}
            </Typography>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(progress.numDays, 7)}, 1fr)`, gap: 0.5, mb: 1.5 }}>
            {Array.from({ length: progress.numDays }, (_, i) => i + 1).map((dayNum) => {
              const exec = (progress.executions ?? []).find((e) => e.DayNumber === dayNum);
              const { sent, failed, title } = getCampaignDayState(exec, dayNum, lastSentDay);
              const colorBg = sent ? 'rgba(34,197,94,0.18)' : failed ? 'rgba(239,68,68,0.18)' : 'var(--color-bg)';
              const colorFg = sent ? '#22c55e' : failed ? '#ef4444' : 'var(--color-text-subtle)';
              return (
                <Box
                  key={dayNum}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    py: 0.75, borderRadius: 1, bgcolor: colorBg, color: colorFg,
                    border: '1px solid var(--color-border)', opacity: on ? 1 : 0.5,
                  }}
                  title={title}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>D{dayNum}</Typography>
                  <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center' }}>
                    {sent ? <CheckCircleIcon sx={{ fontSize: 14 }} />
                      : failed ? <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                        : <ScheduleIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
            {progress.lastSentAt
              ? <>Last sent: <strong>Day {lastSentDay}</strong> on {new Date(progress.lastSentAt).toLocaleString()}</>
              : 'No messages sent yet.'}
          </Typography>

          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon />}
            disabled={busy || !hasMobile || allSent || !readiness.canActivate}
            onClick={send}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {busy ? 'Sending...' : allSent ? `All ${progress.numDays} day(s) sent` : `Send Day ${lastSentDay + 1} now`}
          </Button>
          {!hasMobile && (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mt: 0.5 }}>
              Add a mobile number to enable sending.
            </Typography>
          )}
          {hasMobile && !allSent && !readiness.canActivate && on && (
            <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
              {readinessWarningText(readiness.reasons)}
            </Typography>
          )}
          {info && <Alert severity="success" sx={{ mt: 1, py: 0.5 }}>{info}</Alert>}
          {error && <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>{error}</Alert>}
        </CardContent>
      </Card>

      <Dialog
        open={showResetDialog}
        onClose={() => !busy && setShowResetDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Reset {progress.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
            Choose which day the next message should be. Days before the chosen
            day will be marked as already sent; days from the chosen day onward
            will be reset to pending.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Next send</InputLabel>
            <Select
              value={resetNextDay}
              label="Next send"
              onChange={(e) => setResetNextDay(Number(e.target.value))}
            >
              {Array.from({ length: progress.numDays }, (_, i) => i + 1).map((dayNum) => (
                <MenuItem key={dayNum} value={dayNum}>
                  {dayNum === 1 ? 'Day 1 (full reset)' : `Day ${dayNum}`}
                </MenuItem>
              ))}
              <MenuItem value={progress.numDays + 1}>
                Mark all {progress.numDays} as sent (skip series)
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowResetDialog(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={reset}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {busy ? 'Resetting...' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
